import type { SetTableTag } from '../../../types/KidPdfGeneratorV2.types';

/** True when the cell value is populated from FIG/COST/FUND markers (read-only). */
export function isPresetTableInjectedCell(
  tag: SetTableTag,
  rowIndex: number,
  colIndex: number,
): boolean {
  switch (tag) {
    case 'composition_of_costs_table':
      return rowIndex >= 1 && colIndex === 2;
    case 'costs_over_time_table':
      if (rowIndex === 0) return colIndex === 0 || colIndex === 2;
      return colIndex >= 1;
    case 'performance_scenarios_table':
      if (rowIndex === 0) return colIndex === 3;
      return colIndex >= 2;
    default:
      return false;
  }
}

/** Merge marker-built values into table content, preserving user edits elsewhere. */
export function mergeInjectedPresetTableContent(
  tag: SetTableTag,
  markerBuiltContent: string[][],
  existingContent?: string[][],
): string[][] {
  if (markerBuiltContent.length === 0) return existingContent ?? [];

  const existing = existingContent ?? [];
  const rowCount = Math.max(markerBuiltContent.length, existing.length);
  const colCount = Math.max(
    0,
    ...markerBuiltContent.map((row) => row.length),
    ...existing.map((row) => row.length),
  );

  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const builtRow = markerBuiltContent[rowIndex];
    const existingRow = existing[rowIndex];

    return Array.from({ length: colCount }, (_, colIndex) => {
      const builtValue = builtRow?.[colIndex];
      const existingValue = existingRow?.[colIndex];

      // Only overwrite injected cells that exist in the marker template.
      if (
        builtRow != null &&
        colIndex < builtRow.length &&
        isPresetTableInjectedCell(tag, rowIndex, colIndex)
      ) {
        return builtValue ?? '';
      }

      if (existingValue !== undefined) return existingValue;
      return builtValue ?? '';
    });
  });
}
